import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, Sparkles, Tags } from 'lucide-react';
import { fadeInUp } from '../../lib/animations';
import { formatDate, type PostRecommendation } from '../../lib/posts';

interface BlogPostRecommendationsProps {
  recommendations: PostRecommendation[];
  className?: string;
}

const recommendationLabels: Record<PostRecommendation['kind'], string> = {
  recent: 'Mais recente',
  related: 'Relacionado ao tema',
  previous: 'Artigo anterior',
};

const recommendationIcons: Record<PostRecommendation['kind'], React.ComponentType<{ className?: string; strokeWidth?: number }>> = {
  recent: Sparkles,
  related: Tags,
  previous: ArrowRight,
};

const BlogPostRecommendations: React.FC<BlogPostRecommendationsProps> = ({
  recommendations,
  className = '',
}) => {
  if (recommendations.length === 0) return null;

  return (
    <motion.aside
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      className={className}
    >
      <div className="lg:sticky lg:top-28 space-y-4">
        <div className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/20 bg-amber-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-400">
            Continue lendo
          </span>
          <h2 className="mt-4 font-display text-2xl font-bold text-white">Outros artigos para você</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-400">
            Selecionamos mais conteúdos do blog Gas2 para você aprofundar a leitura.
          </p>
        </div>

        {recommendations.map(({ kind, post }) => {
          const Icon = recommendationIcons[kind];

          return (
            <Link
              key={`${kind}-${post.slug}`}
              to={`/blog/${post.slug}`}
              className="group block rounded-2xl border border-slate-800 bg-slate-900/60 p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-amber-400/40 hover:bg-slate-900"
            >
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/20 bg-amber-400/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-amber-400">
                  <Icon className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                  {recommendationLabels[kind]}
                </span>
                <ArrowRight
                  className="h-4 w-4 shrink-0 text-slate-500 transition-colors group-hover:text-amber-400"
                  strokeWidth={2}
                />
              </div>

              <h3 className="font-display text-lg font-semibold leading-snug text-white transition-colors group-hover:text-amber-400">
                {post.title}
              </h3>

              <p className="mt-2 text-sm leading-relaxed text-slate-400 line-clamp-3">{post.tldr}</p>

              <div className="mt-4 flex items-center gap-1.5 text-xs text-slate-500">
                <Calendar className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
                {formatDate(post.date)}
              </div>
            </Link>
          );
        })}
      </div>
    </motion.aside>
  );
};

export default BlogPostRecommendations;

